import React, { useState } from "react";
import Head from "next/head";
import classNames from "classnames/bind";

import { log } from "../../lib/utils/commons";
import styles from "./GdprProxyModal.module.scss";
import {
  Heading,
  Overlay,
  Button,
  ButtonGroup,
  Text,
  Prose
} from "../elements";
const cx = classNames.bind(styles);

const DEFAULTS = {
  title: "Your privacy",
  message:
    "We have to collect some data when you use this website so it works and is secure. We'd also like your consent to collect data to study how people use our site. Doing so helps us improve our services and allows us to tailor the marketing you see on our site. Select 'Accept cookies' to agree or 'Cookie settings' to choose which cookies we use. You can change your mind at any time by clicking the 'Manage Cookies' link.",
  cta1: "Accept cookies",
  cta2: "Cookie settings"
};

const GDPR_WIDGET_ID = "gdpr-widget";
const GDPR_CTA_1_SELECTOR = "[data-testid='acceptAll']";
const GDPR_CTA_2_SELECTOR = "[data-testid='manageConsent']";

const tracking = {
  "data-wa-link": "",
  "data-action": "interacted",
  "data-object": "content",
  "data-ui-action": "clicked",
  "data-ui-object": "button",
  "data-ui-object-detail": "",
  "data-ui-access-point": "",
  "data-sku": ""
};

const findAndClickWidgetElement = (selector, action) => {
  let i = 0;
  const interval = setInterval(() => {
    if (!document.getElementById(GDPR_WIDGET_ID)) {
      i++;
    } else {
      try {
        document.querySelector(selector).click();
      } catch (error) {
        log.error(
          `Proxy modal error. EVENT_NAME=GDPR_PROXY_MODAL_ERROR - ${error.stack}`
        );
      } finally {
        document.getElementById("gdpr-widget-hide")?.remove();
        clearInterval(interval);
        action();
      }
    }
    if (i === 60) {
      // 6 seconds bail out
      clearInterval(interval);
      action();
    }
  }, 100);
};

const Header = () => (
  <Head>
    <script
      dangerouslySetInnerHTML={{
        __html: `
        if (document.cookie.includes('gdpr=1')) {
          // Hide GDPR Overlay Modal when gdpr cookie is set, 
          // i.e. show it only on first visit. 
          var style = document.createElement('style');
          style.id = 'gdpr-overlay-modal-hide';
          style.dataset.testId = 'gdpr-overlay-modal-hide';
          style.innerHTML = '#gdpr-overlay-modal {display: none}';
          document.documentElement.appendChild(style);
        } else {
          // Hide the GDPR Consent Manager when gdpr cookie is not set, 
          // i.e. show it only after interacting with Overlay Modal.
          var style = document.createElement('style');
          style.id = 'gdpr-widget-hide';
          style.dataset.testId = 'gdpr-widget-hide';
          style.innerHTML = '#gdpr-widget {display: none}';
          document.documentElement.appendChild(style);
        }`
      }}
    ></script>
  </Head>
);

/**
 * GDPR Proxy modal replaces the #gdpr-widget, intended to improve LCP. To do so it checks 'gdpr=1' in cookie and show/hide the proxy-modal in head script.
 *
 */
const GdprProxyModal = ({
  title = DEFAULTS.title,
  message = DEFAULTS.message,
  cta1 = DEFAULTS.cta1,
  cta2 = DEFAULTS.cta2
}) => {
  // Show GDPR banner by default
  const [showBanner, setShowBanner] = useState(true);

  return (
    <>
      <Header />
      {showBanner && (
        <div id="gdpr-overlay-modal" className={cx("modal")}>
          <Overlay />
          <div className={cx("dialog")}>
            <Prose>
              <Heading node="h2" style="h4">
                {title}
              </Heading>
              <Text>{message}</Text>
              <ButtonGroup>
                <Button
                  pos={1}
                  onClick={() => {
                    findAndClickWidgetElement(GDPR_CTA_1_SELECTOR, () =>
                      setShowBanner(false)
                    );
                  }}
                  {...{
                    ...tracking,
                    ...{
                      "data-wa-link": "gdprWidgetCloseButtonClick",
                      "data-ui-object-detail": `${cta1}`
                    }
                  }}
                  data-testid={"gdpr-proxy-cta-1"}
                >
                  <span>{cta1}</span>
                </Button>
                <Button
                  pos={2}
                  variant="tertiary"
                  onClick={() => {
                    findAndClickWidgetElement(GDPR_CTA_2_SELECTOR, () =>
                      setShowBanner(false)
                    );
                  }}
                  {...{
                    ...tracking,
                    ...{
                      "data-wa-link": "gdprWidgetManageConsentButtonClick",
                      "data-ui-object-detail": `${cta2}`
                    }
                  }}
                  data-testid={"gdpr-proxy-cta-2"}
                >
                  <span>{cta2}</span>
                </Button>
              </ButtonGroup>
            </Prose>
          </div>
        </div>
      )}
    </>
  );
};

export { GdprProxyModal as default };
